import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Bell, Mail, MessageSquare, Smartphone, Loader2, Calendar, CheckSquare, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import Navigation from '../components/Navigation';

import { API, API_BASE } from '../config/api';
const BACKEND_URL = API_BASE;

function NotificationSettings({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    notification_email: true,
    notification_sms: false,
    notification_push: true
  });
  const [reminderPreferences, setReminderPreferences] = useState({
    enabled: true,
    events: true,
    upcoming_tasks: true,
    overdue_tasks: true,
    unread_messages: true
  });

  useEffect(() => {
    fetchPreferences();
    fetchReminderPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await fetch(`${API}/users/me/notification-preferences`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch preferences');
      const data = await response.json();
      setPreferences(data);
    } catch (error) {
      console.error('Error fetching preferences:', error);
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const fetchReminderPreferences = async () => {
    try {
      const response = await fetch(`${API}/users/me/reminder-preferences`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setReminderPreferences(data);
      }
    } catch (error) {
      console.error('Error fetching reminder preferences:', error);
    }
  };

  const updatePreference = async (key, value) => {
    const previousValue = preferences[key];
    
    // Optimistically update the UI
    setPreferences(prev => ({ ...prev, [key]: value }));
    setSaving(true);

    try {
      const response = await fetch(`${API}/users/me/notification-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [key]: value })
      });

      if (!response.ok) throw new Error('Failed to update preferences');
      
      const updatedPreferences = await response.json();
      setPreferences(updatedPreferences);
      toast.success('Preference updated');
    } catch (error) {
      console.error('Error updating preference:', error);
      // Revert on error
      setPreferences(prev => ({ ...prev, [key]: previousValue }));
      toast.error('Failed to update preference');
    } finally {
      setSaving(false);
    }
  };

  const updateReminderPreference = async (key, value) => {
    const previousValue = reminderPreferences[key];
    
    // Optimistically update the UI
    setReminderPreferences(prev => ({ ...prev, [key]: value }));
    setSaving(true);

    try {
      const response = await fetch(`${API}/users/me/reminder-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...reminderPreferences, [key]: value })
      });

      if (!response.ok) throw new Error('Failed to update reminder preferences');
      
      const updatedPreferences = await response.json();
      setReminderPreferences(updatedPreferences.preferences);
      toast.success('Reminder preference updated');
    } catch (error) {
      console.error('Error updating reminder preference:', error);
      // Revert on error
      setReminderPreferences(prev => ({ ...prev, [key]: previousValue }));
      toast.error('Failed to update preference');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCFE]">
        <Navigation user={user} onLogout={handleLogout} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#80298F]" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCFE]">
      <Navigation user={user} onLogout={handleLogout} />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="text-gray-600 hover:text-[#80298F]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Bell className="h-7 w-7 text-[#80298F]" />
            Notification Settings
          </h1>
          <p className="text-gray-600 mt-2">
            Choose how you want to receive notifications about your cases, messages, and updates.
          </p>
        </div>

        {/* Notification Preferences Card */}
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="text-lg">Notification Channels</CardTitle>
            <CardDescription>
              Enable or disable notifications for each channel. Changes are saved automatically.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Email Notifications */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Mail className="h-5 w-5 text-[#80298F]" />
                </div>
                <div>
                  <Label htmlFor="email-notifications" className="text-base font-medium text-gray-900">
                    Email Notifications
                  </Label>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Receive updates about case status, messages, and appointments via email
                  </p>
                </div>
              </div>
              <Switch
                id="email-notifications"
                checked={preferences.notification_email}
                onCheckedChange={(checked) => updatePreference('notification_email', checked)}
                disabled={saving}
              />
            </div>

            {/* SMS Notifications */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <MessageSquare className="h-5 w-5 text-[#80298F]" />
                </div>
                <div>
                  <Label htmlFor="sms-notifications" className="text-base font-medium text-gray-900">
                    SMS Notifications
                  </Label>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Get text messages for urgent updates and appointment reminders
                  </p>
                </div>
              </div>
              <Switch
                id="sms-notifications"
                checked={preferences.notification_sms}
                onCheckedChange={(checked) => updatePreference('notification_sms', checked)}
                disabled={saving}
              />
            </div>

            {/* Push Notifications */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Smartphone className="h-5 w-5 text-gray-400" />
                </div>
                <div>
                  <Label htmlFor="push-notifications" className="text-base font-medium text-gray-400">
                    Push Notifications
                  </Label>
                  <p className="text-sm text-gray-400 mt-0.5">
                    Coming soon
                  </p>
                </div>
              </div>
              <Switch
                id="push-notifications"
                checked={false}
                disabled={true}
              />
            </div>
          </CardContent>
        </Card>

        {/* Reminder Preferences Card */}
        <Card className="shadow-md mt-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="h-5 w-5 text-[#80298F]" />
              Automated Reminders
            </CardTitle>
            <CardDescription>
              Control which types of automated reminders you receive. Reminders help you stay on top of important deadlines and updates.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Master Toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-[#80298F]/5 border border-[#80298F]/20">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Bell className="h-5 w-5 text-[#80298F]" />
                </div>
                <div>
                  <Label htmlFor="reminders-enabled" className="text-base font-medium text-gray-900">
                    Enable All Reminders
                  </Label>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Turn off to stop receiving all automated reminders
                  </p>
                </div>
              </div>
              <Switch
                id="reminders-enabled"
                checked={reminderPreferences.enabled}
                onCheckedChange={(checked) => updateReminderPreference('enabled', checked)}
                disabled={saving}
              />
            </div>

            {reminderPreferences.enabled && (
              <>
                {/* Event Reminders */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Calendar className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <Label htmlFor="event-reminders" className="text-base font-medium text-gray-900">
                        Calendar Event Reminders
                      </Label>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Get reminded about upcoming appointments, hearings, and meetings
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="event-reminders"
                    checked={reminderPreferences.events}
                    onCheckedChange={(checked) => updateReminderPreference('events', checked)}
                    disabled={saving}
                  />
                </div>

                {/* Upcoming Task Reminders */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <CheckSquare className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <Label htmlFor="upcoming-task-reminders" className="text-base font-medium text-gray-900">
                        Upcoming Task Reminders
                      </Label>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Get notified before task due dates arrive
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="upcoming-task-reminders"
                    checked={reminderPreferences.upcoming_tasks}
                    onCheckedChange={(checked) => updateReminderPreference('upcoming_tasks', checked)}
                    disabled={saving}
                  />
                </div>

                {/* Overdue Task Reminders */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <AlertTriangle className="h-5 w-5 text-orange-500" />
                    </div>
                    <div>
                      <Label htmlFor="overdue-task-reminders" className="text-base font-medium text-gray-900">
                        Overdue Task Reminders
                      </Label>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Receive reminders for tasks that are past their due date
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="overdue-task-reminders"
                    checked={reminderPreferences.overdue_tasks}
                    onCheckedChange={(checked) => updateReminderPreference('overdue_tasks', checked)}
                    disabled={saving}
                  />
                </div>

                {/* Unread Message Reminders */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <MessageSquare className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <Label htmlFor="unread-message-reminders" className="text-base font-medium text-gray-900">
                        Unread Message Reminders
                      </Label>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Get reminded about messages you haven't read yet
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="unread-message-reminders"
                    checked={reminderPreferences.unread_messages}
                    onCheckedChange={(checked) => updateReminderPreference('unread_messages', checked)}
                    disabled={saving}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Info Note */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Even with notifications disabled, you can always check your updates by logging into the portal. Critical security notifications will always be sent via email.
          </p>
        </div>

        {/* Saving indicator */}
        {saving && (
          <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Saving...
          </div>
        )}
      </main>
    </div>
  );
}

export default NotificationSettings;
